import { labels } from '@/app/constants/labels';
import Tab from '@ixigo/iui/Tab';
import TabList from '@ixigo/iui/TabList';
import TabPanel from '@ixigo/iui/TabPanel';
import Tabs from '@ixigo/iui/Tabs';
import Typography from '@ixigo/iui/Typography';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { EmiOption } from '../../domain/payment';
import usePaymentStore from '../../store';
import FullScreenAddNewCard from '../CardsSection/NewCard/FullScreenAddNewCard';
import EMIPlanAccordion from './EmiPlanAccordion';
import useEmiPlanSelection from './useEmiPlanSelection';

const EmiPlansTabs = ({ isExpanded }: { isExpanded?: boolean }) => {
	const { emiOptions, emiSection, selectedPaymentOption, setSelectedPaymentOption } =
		usePaymentStore(
			useShallow(({ emiOptions, emiSection, selectedPaymentOption, setSelectedPaymentOption }) => ({
				emiOptions,
				emiSection,
				selectedPaymentOption,
				setSelectedPaymentOption,
			})),
		);
	const { paymentReference = '', pgFeeText, amount = 0 } = emiSection ?? {};
	const { selectedEmiPlan, setSelectedEmiPlan } = useEmiPlanSelection(null);
	const [showAddCard, setShowAddCard] = useState(false);

	if (!emiOptions?.length) return null;
	const totalAmountText = labels.TOTAL_AMOUNT_TEXT(amount);

	const selectedBank = selectedPaymentOption as EmiOption;
	const onBankChange = (bank: EmiOption) => {
		if (selectedPaymentOption === bank) {
			setSelectedPaymentOption(null);
		} else {
			setSelectedPaymentOption(bank);
		}
		setSelectedEmiPlan(bank.emiInfo[0]);
	};

	const creditCardEmis = emiOptions?.filter((option) => option.cardType === 'CREDIT');
	const debitCardEmis = emiOptions?.filter((option) => option.cardType === 'DEBIT');

	const tabsArray = [labels.CREDIT_CARD_TEXT, labels.DEBIT_CARD_TEXT];

	return (
		<div>
			{isExpanded ? (
				<>
					<Typography variant="body-md" fontWeight="bold" className="py-10">
						{labels.ADD_NEW_CARD}
					</Typography>
					<EMIPlanAccordion
						onBankChange={onBankChange}
						emiOptions={emiOptions}
						onAddCardCTA={() => setShowAddCard(true)}
						selectedBank={selectedBank}
						selectedEmiPlan={selectedEmiPlan}
						onSelectEmiPlan={setSelectedEmiPlan}
						type="EMI"
					/>
				</>
			) : (
				<Tabs className="w-full" defaultValue={tabsArray[0]}>
					<div>
						<TabList className="mt-10 w-full gap-10">
							{tabsArray.map((option) => (
								<Tab
									variant="outlined"
									value={option}
									key={option}
									className="body-sm w-full justify-center font-bold"
								>
									{option}
								</Tab>
							))}
						</TabList>
					</div>
					{tabsArray.map((option) => (
						<TabPanel value={option} key={option} className="max-w-[530px] overflow-y-auto pb-0">
							<EMIPlanAccordion
								onBankChange={onBankChange}
								emiOptions={option === 'Credit Card' ? creditCardEmis : debitCardEmis}
								onAddCardCTA={() => setShowAddCard(true)}
								selectedBank={selectedBank}
								selectedEmiPlan={selectedEmiPlan}
								onSelectEmiPlan={setSelectedEmiPlan}
								type={option}
							/>
						</TabPanel>
					))}
				</Tabs>
			)}
			{selectedBank && selectedEmiPlan && (
				<FullScreenAddNewCard
					showAddNewCard={showAddCard}
					paymentReference={paymentReference}
					subText={pgFeeText ?? totalAmountText}
					onBack={() => setShowAddCard(false)}
					selectedEmiBank={selectedBank}
					selectedEmiPlan={selectedEmiPlan}
				/>
			)}
		</div>
	);
};

export default EmiPlansTabs;
